import { unreviewedCount, type DrivenRow } from '@agavi/blocks';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';


import { useAnswerRefScope } from './answer-ref-context';
import { useBlockIdentity } from './fired-states';
import { EditorSectionHeading } from './input-block-frame';
import { useEditorVersionInForce } from './use-block-versions';
import { useDriverRows } from './use-driver-rows';

import type { Block } from '@/types/experience';

/**
 * WHAT THIS BLOCK VARIES WITH — the section the link control lives in, for both kinds of link.
 *
 * A block follows a scenario in this task, or is driven by an answer in an earlier one, or
 * neither. The two are mutually exclusive and answer the same question, so they share one
 * heading rather than each growing their own: a teacher asking "what makes this block change"
 * should find one place, and the control that answers it should be in that place.
 *
 * ## Why the link moves up here at all
 *
 * It starts in the QUESTION heading because that is the only heading an unlinked block has.
 * Once there IS a link, leaving it there puts the control that says "this answers Text 2"
 * underneath the thing it names — and crams it against the version pager, in a heading narrow
 * enough that a menu opening from it covered the content it was about. Reported in review as
 * difficult to navigate.
 *
 * Up here there is a row to itself and nothing beneath it yet, so the picker opens over empty
 * space. The scenario half already worked this way; the DRIVEN half never got the section, so
 * its control stayed in the cramped heading for ever.
 *
 * ## Twelve copies, one component
 *
 * Every input block had the same five lines of JSX, differing only in how it reached its own
 * scenario state. That is the shape that put six blocks a commit behind on the link control
 * twice already. Whether the section appears for a DRIVEN block is decided here, from the
 * context the component already sits in, so no block had to learn about it.
 */
/**
 * WHETHER THIS BLOCK IS DRIVEN BY AN EARLIER ANSWER, read from the context it sits in.
 *
 * Shared by the two components below BECAUSE THEY ARE COMPLEMENTS: the link belongs in the
 * Varies-With section when there is one and in the QUESTION heading when there is not, and a
 * block must never show both. Two copies of this test is how it came to show both — the
 * section learned about driven blocks and the heading's own `!activeScenario` did not, so a
 * driven block grew a second link control and the heading overflowed into a horizontal
 * scrollbar.
 *
 * `drivenBy` alone, not whether it resolves: an address that no longer points anywhere still
 * means the block is MEANT to vary with something, and the section is where the control that
 * repairs it lives.
 */
function useIsDriven(): boolean {
  const scope = useAnswerRefScope();
  const identity = useBlockIdentity();
  const self = identity?.blockId
    ? scope?.tasks.find(tk => tk.id === scope.currentTaskId)
      ?.blocks?.find((b: Block) => b.id === identity.blockId)
    : undefined;
  return !!(self?.data as { drivenBy?: string } | undefined)?.drivenBy;
}

/**
 * THIS BLOCK FOLLOWS A SCENARIO — `followsBlockId` alone, and whether it still lands.
 *
 * The same rule `useIsDriven` states above, applied to the axis that never got it: an address
 * that no longer points anywhere still means the block is MEANT to vary with something, and
 * the section is where the control that repairs it lives.
 *
 * Every block instead passed `following={!!activeScenario}` — the RESOLVED leader. A link
 * naming a block outside this task therefore rendered nothing at all: no section, no control,
 * no issue, and the teacher could neither see it nor clear it. Reported as "there is a block I
 * left on there that is anchored to something, but i can't tell what".
 *
 * Those links are not supposed to exist — a scenario link lives inside one task — and the
 * copy paths that used to make them have been fixed at both ends (`paste-blocks` drops the
 * link when the leader does not come along, `BLOCK_ID_KEYS_IN_DATA` does the same on the
 * server's duplicate). What was missing is the way OUT for the ones already stored.
 *
 * Read here rather than passed, for the reason the driven half is: fourteen call sites is
 * fourteen places to remember a rule, and the one that forgot would be a block with an
 * invisible link again.
 */
function useFollowedLeader(): { linked: boolean; lost: boolean } {
  const scope = useAnswerRefScope();
  const identity = useBlockIdentity();
  const here = scope?.tasks.find(tk => tk.id === scope.currentTaskId);
  const self = identity?.blockId
    ? here?.blocks?.find((b: Block) => b.id === identity.blockId)
    : undefined;
  const leader = (self?.data as { followsBlockId?: string } | undefined)?.followsBlockId;
  if (!leader) return { linked: false, lost: false };
  return { linked: true, lost: !here?.blocks?.some((b: Block) => b.id === leader) };
}

/**
 * The link control in the QUESTION heading — where it lives until there is a section for it.
 *
 * An unlinked block has no Varies-With section, so the question heading is the only heading
 * there is and the control starts there. The moment a link exists the section appears and
 * takes the control, which is the jump that puts it beside what it created.
 *
 * This is the half that must go SILENT then, and the reason it is a component rather than the
 * `{!activeScenario && link}` each block used to write: that test knew about scenarios only.
 */
export function QuestionHeadingLink({ following, linkMenu }: {
  following: boolean;
  linkMenu: React.ReactNode;
}) {
  const driven = useIsDriven();
  /* A LOST LINK STILL OWNS THE CONTROL. Without this the heading keeps it while the section
     below also shows one, which is the double control the section's own note records. */
  const follows = useFollowedLeader();
  if (following || driven || follows.linked) return null;
  return <>{linkMenu}</>;
}

export function VariesWithSection({ following, linkMenu, stimulus }: {
  /** This block follows a scenario in its own task. */
  following: boolean;
  /** The link control itself, built by the block — it owns the callbacks. */
  linkMenu: React.ReactNode;
  /** `ScenarioStimulus`, which renders the leader's variant OR the driven row on its own. */
  stimulus: React.ReactNode;
}) {
  const { t } = useTranslation();
  /* The same predicates `QuestionHeadingLink` reads, so the two can never both show. */
  const driven = useIsDriven();
  const follows = useFollowedLeader();

  if (!following && !driven && !follows.linked) return null;
  return (
    <>
      <EditorSectionHeading
        label={t('blocks.areas.variesWith')}
        /* First heading in the panel whenever it is here at all. */
        divider={false}
        action={linkMenu}
      />
      {/* WHY THERE IS A SECTION AND NOTHING IN IT. A link to a block in another task resolves
          to no leader, so no stimulus and no rows follow it — without a line saying so the
          section reads as broken rendering rather than as a link to repair. The control that
          repairs it is the one in the heading above: setting it to None clears the link. */}
      {follows.lost && (
        <p className='flex items-start gap-1.5 text-xs text-ws-warning-fg'>
          <AlertTriangle aria-hidden='true' className='mt-0.5 h-3.5 w-3.5 shrink-0' />
          {t('blocks.scenario.leaderOutsideTask')}
        </p>
      )}
      {stimulus}
    </>
  );
}

/**
 * WHICH ROW OF A DRIVEN AXIS THE EDITOR IS ON, and whether it is still a copy.
 *
 * One derivation for every surface that needs it. The alternative is each block working out
 * its own row label and copy mark, which is the duplication that has already produced a second
 * link control and a stale collision warning inside this one feature.
 *
 * The EDITOR's pointer, never the test one: this names the row a teacher is authoring. The
 * fallback to the first version is `pickVersion`'s own rule, so the box and the pager can
 * never disagree about which row is on screen.
 */
export function useDrivenRowInForce(): {
  versionId: string;
  /**
   * THE ROW ITSELF, not just its name.
   *
   * `DrivenRowStimulus` renders the answer the row was written for, and an option can be a
   * photograph, a sound or an equation — none of which survives `label`, which is stripped
   * plain text. Carried from the derivation that already has it rather than looked up again:
   * a second lookup is the two-readers split this feature has produced twice.
   */
  row: DrivenRow;
  /**
   * THE WHOLE AXIS, in the driver's own order — what the editor's pager walks.
   *
   * From the derivation that already built it, for the reason `row` is: a second
   * `useDriverRows` call is a second answer to "what are the rows", and the blank row is
   * exactly the kind of member a second derivation drops.
   */
  rows: DrivenRow[];
  /** The task whose variable scope the option's chips resolve in — see `useDriverRows`. */
  driverTaskId?: string;
  label: string;
  kind: 'option' | 'other' | 'blank';
  unreviewed?: boolean;
  unreviewedCount: number;
} | null {
  const scope = useAnswerRefScope();
  const identity = useBlockIdentity();
  const blockId = identity?.blockId;

  const self = blockId
    ? scope?.tasks.find(tk => tk.id === scope.currentTaskId)
      ?.blocks?.find((b: Block) => b.id === blockId)
    : undefined;
  /* THE SAME POINTER THE REST OF THE EDITOR IS ON. Read straight from the store this named the
     first row forever: a driven block never writes the editor pointer, the driver IS the
     pointer, and the override lived only in `useBlockVersions`. Reported as "the ghost box in
     editor is stuck on the first answer". */
  const { editorVersionId } = useEditorVersionInForce(self);
  const data = self?.data as {
    drivenBy?: string;
    versions?: { id: string; drivenRowId?: string; unreviewed?: boolean }[];
  } | undefined;

  /* ONE derivation of the axis's rows, shared with the projection, the issue list and the
     stimulus — see `useDriverRows`. It is also what makes an ALIAS link show a row here: the
     union has rows of its own, and no driving BLOCK to look one up on. */
  const { rows, rowScopeTaskId } = useDriverRows(data?.drivenBy);
  const versions = data?.versions;
  if (!rows || !versions?.length) return null;

  const version = versions.find(v => v.id === editorVersionId) ?? versions[0]!;
  const row = rows.find(r => r.id === version.drivenRowId);
  if (!row) return null;

  return {
    versionId: version.id,
    row,
    rows,
    driverTaskId: rowScopeTaskId,
    label: row.label,
    kind: row.kind,
    unreviewed: version.unreviewed,
    /* Across the whole axis, not just this row — a teacher who never pages to row 5 would
       otherwise never learn row 5 was never decided. `ScenarioRowFrame`'s own note.
       STRANDED VERSIONS EXCLUDED: a version whose row is gone is parked data, and the badge
       would point at something the pager cannot even reach. `syncDrivenVersions` used to do
       this by clearing the flag as it parked the version; it no longer does, because the
       projection needs the flag to survive to know a bank is safe to discard on a re-point. */
    unreviewedCount: unreviewedCount(
      versions.filter(v => rows.some(r => r.id === v.drivenRowId))),
  };
}
